import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

interface Props {
  rejetees: number;           // lignes en erreur (cumulées sur le fichier)
  valides: number;            // lignes valides restant à importer (0 = aucune)
  validesImportees: boolean;  // les lignes valides viennent d'être importées
  onTelechargerErreurs: () => void;
  onTelechargerValides: () => void;
}

const pl = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

/**
 * Import refusé à cause de lignes précises : fichier « erreurs » (lignes refusées + motif)
 * et fichier « valides » (lignes prêtes à importer). Voir useImportExcel / utils/importExcel.
 */
const ImportRejetsPanel: React.FC<Props> = ({
  rejetees, valides, validesImportees, onTelechargerErreurs, onTelechargerValides,
}) => {
  if (rejetees === 0) return null;

  const titre = validesImportees
    ? `${pl(rejetees, 'ligne')} en erreur non importée${rejetees > 1 ? 's' : ''}`
    : valides > 0
      ? `Fichier séparé : ${pl(rejetees, 'ligne')} en erreur · ${pl(valides, 'ligne')} valide${valides > 1 ? 's' : ''}`
      : `${pl(rejetees, 'ligne')} en erreur`;

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight={600} mb={0.5}>{titre}</Typography>
      <Typography variant="body2">
        Lignes en erreur : le motif est indiqué dans la colonne « erreur ». Corrigez-les, puis importez ce
        fichier tel quel.
        {!validesImportees && valides > 0 && ' Lignes valides : reprises sans modification, prêtes à importer.'}
      </Typography>
      <Box display="flex" gap={1} mt={1} flexWrap="wrap">
        <Button size="small" variant="outlined" color="inherit" startIcon={<DownloadIcon />} onClick={onTelechargerErreurs}>
          Lignes en erreur ({rejetees})
        </Button>
        {!validesImportees && valides > 0 && (
          <Button size="small" variant="outlined" color="inherit" startIcon={<DownloadIcon />} onClick={onTelechargerValides}>
            Lignes valides ({valides})
          </Button>
        )}
      </Box>
    </Alert>
  );
};

/** Bouton de pied de fenêtre : importe seulement les lignes valides (tout ou rien sur ce lot). */
export const ImporterValidesButton: React.FC<{ valides: number; importing: boolean; onClick: () => void }> = ({
  valides, importing, onClick,
}) => (
  <Button
    variant="contained"
    disabled={importing}
    onClick={onClick}
    startIcon={importing ? <CircularProgress size={16} color="inherit" /> : undefined}
  >
    {importing ? 'Import en cours…' : valides === 1 ? 'Importer la ligne valide' : `Importer les ${valides} lignes valides`}
  </Button>
);

export default ImportRejetsPanel;
